import i18n from '@/utils/i18n';
import OfflineSyncIcon from '@assets/icons/offline-sync.svg';
import React, { useState } from 'react';
import { ActionSheet } from '@/components/action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useSelectedOfflineRestaurantUUIDs } from '@/contexts/offline-download-management-context';
import { View } from 'react-native';

const HIT_SLOP = {
  bottom: 8,
  left: 16,
  right: 8,
  top: 8,
};

interface Props {
  isDownloading: boolean;
  isManagingOfflineAccounts: boolean;
  setIsManagingOfflineAccounts: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OfflineManagementButton: React.FC<Props> = ({
  isDownloading,
  isManagingOfflineAccounts,
  setIsManagingOfflineAccounts,
}) => {
  const [styles, theme] = useStyles();
  const selectedOfflineRestaurantUUIDs = useSelectedOfflineRestaurantUUIDs();

  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  return (
    <>
      <Button
        accessibilityLabel="offline_management_button"
        accessibilityRole="button"
        disabled={isDownloading}
        hitSlop={HIT_SLOP}
        Icon={OfflineSyncIcon}
        iconProps={{
          fill: isDownloading
            ? theme.sk.color.icon.disabled
            : isManagingOfflineAccounts
              ? theme.sk.color.icon.ondark
              : theme.sk.color.icon.primary,
        }}
        onPress={() => {
          if (
            isManagingOfflineAccounts &&
            selectedOfflineRestaurantUUIDs.size > 0
          ) {
            setShowExitConfirmation(true);
          } else {
            setIsManagingOfflineAccounts((prev) => !prev);
          }
        }}
        rounded
        style={[
          styles.offlineButton,
          {
            backgroundColor:
              isManagingOfflineAccounts && !isDownloading
                ? theme.sk.color.bg.button.primary.default
                : theme.sk.color.bg.secondary.default,
          },
        ]}
        testID="offline-management-button"
        trackingData={{
          name: 'location_picker.offline_management_button',
        }}
      />
      <ActionSheet
        actions={[i18n.t('stay'), i18n.t('leave')]}
        dangerIndices={[1]}
        horizontalButtons
        isVisible={showExitConfirmation}
        message={
          <View style={styles.exitConfirmationMessage}>
            <Typography variant="paragraph-base">
              {i18n.t('exit_sync_mode.message')}
            </Typography>
          </View>
        }
        onAction={(_, i) => {
          if (i === 1) {
            setIsManagingOfflineAccounts(false);
          }

          setShowExitConfirmation(false);
        }}
        onClose={() => setShowExitConfirmation(false)}
        title={i18n.t('exit_sync_mode.title')}
        titleStyle={styles.exitConfirmationTitle}
      />
    </>
  );
};

const useStyles = createStyles(() => ({
  exitConfirmationMessage: {
    marginBottom: 24,
  },
  exitConfirmationTitle: {
    alignSelf: 'flex-start',
  },
  offlineButton: {
    height: 32,
    width: 32,
  },
}));
